import React, { useContext, useEffect, useState } from 'react';
import { Container, SpaceBetween } from '@amzn/awsui-components-react';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import ExecutionGroupSelector from './ExecutionGroupSelector';
import TPEErrorWatcher from 'src/components/shared/TPEErrorWatcher';
import { ACTIONS } from 'src/services/calculation-builder/CalculationBuilderReducer';
import { ExecutionGroupDetails } from 'src/models/calculation-builder/ExecutonGroupDetails';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import { isBlockedByEditLock } from 'src/components/shared/edit-locking';

export default function CLIGroupAssignmentContainer(props: {
    loading: boolean;
    // CLI-config edit-lock view mode + whether edit-locking applies (both from
    // CalculationBuilderView). Group assignment mutates the CLI, so it is only editable
    // when the current user holds the lock (viewMode EDITABLE). When the flag is off or the
    // record is ICRS, editLockingApplies is false and the gate is inert (pre-feature behavior).
    viewMode?: string;
    editLockingApplies?: boolean;
}) {
    const { loading, viewMode, editLockingApplies = false } = props;
    const { services, calcBuilderState, calcBuilderDispatch } = useContext(CalculationBuilderContext);
    const { calculation, dataSourceRecords } = calcBuilderState;

    const [loadTime, setLoadTime] = useState(null as string | null);
    const [saveExecutionGroupPayload, setSaveExecutionGroupPayload] = useState(null as unknown as any);
    const [executionGroupDetails, setExecutionGroupDetails] = useState(undefined as ExecutionGroupDetails | undefined);
    const { canEdit: canEditCalcBuilder } = getPermissions(AppModules.CALCULATION_BUILDER);

    const [executionGroupDetailsResult, isGettingExecutionGroupDetails, executionGroupDetailsError] =
        services.calculationsService.getOrAssignExecutionGroup(loadTime, calculation?.calculationNumber);
    const [saveExecutionGroupResult, isSavingExecutionGroup, saveExecutionGroupError] =
        services.calculationsService.saveCalculationExecutionGroup(saveExecutionGroupPayload);
    const [cliExecutionGroups, cliExecutionGroupsLoading, cliExecutionGroupsError] =
        services.calculationsService.getCLIExecutionGroups(loadTime);

    useEffect(() => {
        if (calculation?.calculationNumber) {
            setLoadTime(new Date().toString());
        }
    }, [calculation?.calculationNumber]);

    useEffect(() => {
        if (executionGroupDetailsResult == null) {
            return;
        }
        setExecutionGroupDetails(executionGroupDetailsResult);
        calcBuilderDispatch(ACTIONS.SET_EXECUTION_GROUP_NUMBER.withPayload(executionGroupDetailsResult.executionGroup));
    }, [executionGroupDetailsResult]);

    useEffect(() => {
        if (saveExecutionGroupResult == null) {
            return;
        }
        setExecutionGroupDetails(saveExecutionGroupResult);
        calcBuilderDispatch(ACTIONS.SET_EXECUTION_GROUP_NUMBER.withPayload(saveExecutionGroupResult.executionGroup));
        let successMsg = `Calculation assigned to Group ${saveExecutionGroupResult.executionGroup} successfully.`;
        if (calculation?.workbookId != null) {
            successMsg += ` ${saveExecutionGroupResult.autoDraftsForActiveCalcsCreated} active calculation(s) and `;
            successMsg += `${saveExecutionGroupResult.autoDraftsForPendingReviewCalcsCreated} pending review calculation(s) `;
            successMsg += `in the workbook ${calculation?.workbookId} (${calculation?.workbookName}) were automatically moved to draft status due to this update.`;
        }
        services.messageService.showSuccessAutoDismissBanner(successMsg, 10000);
        calcBuilderDispatch(ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [saveExecutionGroupResult]);

    useEffect(() => {
        if (saveExecutionGroupError == null) {
            return;
        }
        calcBuilderDispatch(ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [saveExecutionGroupError]);

    // Group assignment is editable only when the user has calc-builder edit permission (respects
    // read-only mode for admin/accounting), no save is in flight, AND the CLI is not blocked by the
    // edit lock (user must hold the lock, i.e. be in edit mode). isBlockedByEditLock is inert when
    // editLockingApplies is false (flag off / ICRS), preserving prior behavior.
    const canEditCLIAttributes =
        canEditCalcBuilder && !isSavingExecutionGroup && !isBlockedByEditLock(viewMode, editLockingApplies);

    const saveExecutionGroup = (executionGroup: string) => {
        setSaveExecutionGroupPayload({
            calculationNumber: calculation?.calculationNumber,
            executionGroup: executionGroup,
        });
    };

    return (
        <Container className="templatesContainer">
            <TPELoadingSpinner loading={loading || isGettingExecutionGroupDetails || cliExecutionGroupsLoading}>
                <ExecutionGroupSelector
                    cliExecutionGroups={cliExecutionGroups || []}
                    executionGroupDetails={executionGroupDetails}
                    canEditCLIAttributes={canEditCLIAttributes}
                    calculation={calculation}
                    saveExecutionGroup={saveExecutionGroup}
                    isSaving={isSavingExecutionGroup}
                    dataSources={dataSourceRecords}
                />
                <TPEErrorWatcher
                    services={services}
                    errors={[executionGroupDetailsError, saveExecutionGroupError, cliExecutionGroupsError]}
                />
            </TPELoadingSpinner>
        </Container>
    );
}
